import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import BuildIcon from '@mui/icons-material/Build';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DownloadIcon from '@mui/icons-material/Download';
import Fade from '@mui/material/Fade';
import Divider from '@mui/material/Divider';
import LogoutIcon from '@mui/icons-material/Logout';
import TerminalIcon from '@mui/icons-material/Terminal';
import ExtensionIcon from '@mui/icons-material/Extension';
import PeopleIcon from '@mui/icons-material/People';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LabelIcon from '@mui/icons-material/Label';
import TaskIcon from '@mui/icons-material/Task';
import FolderIcon from '@mui/icons-material/Folder';
import CloudIcon from '@mui/icons-material/Cloud';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CircularProgress from '@mui/material/CircularProgress';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddBookmarkForm from './forms/AddBookmarkForm';
import CustomAlert from './CustomAlert';

import { useAuth } from '../provider/AuthProvider';
import { useBookmarkRefresh } from '../provider/BookmarkRefreshProvider';
import { useThemeMode } from '../provider/ThemeProvider';
import httpProvider from '../provider/HttpProvider';
import {
    downloadChromeExtension,
    downloadEdgeExtension,
} from '../utils/extensionDownload';

export default function MainMenu() {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [toolsAnchorEl, setToolsAnchorEl] = React.useState(null);
    const [alertInfo, setAlertInfo] = useState({
        message: '',
        severity: 'info',
    });
    const [isDownloadingChrome, setIsDownloadingChrome] = useState(false);
    const [isDownloadingEdge, setIsDownloadingEdge] = useState(false);
    const open = Boolean(anchorEl);
    const toolsOpen = Boolean(toolsAnchorEl);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToolsClick = event => {
        setToolsAnchorEl(event.currentTarget);
    };
    const handleToolsClose = () => {
        setToolsAnchorEl(null);
    };

    const handleDownloadChromeExtension = async () => {
        setIsDownloadingChrome(true);
        try {
            const result = await downloadChromeExtension();
            setAlertInfo({
                message: `Chrome extension downloaded successfully: ${result.filename}`,
                severity: 'success',
            });
        } catch (error) {
            setAlertInfo({
                message: error.message || 'Failed to download Chrome extension',
                severity: 'error',
            });
        } finally {
            setIsDownloadingChrome(false);
        }
    };

    const handleDownloadEdgeExtension = async () => {
        setIsDownloadingEdge(true);
        try {
            const result = await downloadEdgeExtension();
            setAlertInfo({
                message: `Edge extension downloaded successfully: ${result.filename}`,
                severity: 'success',
            });
        } catch (error) {
            setAlertInfo({
                message: error.message || 'Failed to download Edge extension',
                severity: 'error',
            });
        } finally {
            setIsDownloadingEdge(false);
        }
    };

    const { getToken } = useAuth();
    const token = getToken();
    const { triggerRefresh } = useBookmarkRefresh();
    const { mode, toggleMode } = useThemeMode();
    const [overdueCount, setOverdueCount] = useState(0);

    useEffect(() => {
        if (!token) return;
        const today = new Date().toISOString().slice(0, 10);
        httpProvider
            .get(`/api/tasks/?is_completed=false&limit=1000&sort=due_date&sortDesc=false`)
            .then(res => {
                const rows = res.data?.rows ?? [];
                const overdue = rows.filter(t => t.due_date && t.due_date.slice(0, 10) < today);
                setOverdueCount(overdue.length);
            })
            .catch(() => {});
    }, [token]);

    if (token == null) {
        return (
            <p>
                You are not logged in.{' '}
                <small>
                    <Link to="/login">Login</Link>
                </small>
            </p>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
            >
                {/* Add Bookmark Form - primary action */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 40,
                        maxWidth: { xs: '100%', md: 700 },
                    }}
                >
                    <AddBookmarkForm
                        token={token}
                        onAddSuccess={message => {
                            setAlertInfo({ message, severity: 'success' });
                            triggerRefresh();
                        }}
                        onAddError={msg =>
                            setAlertInfo({
                                message: msg.error,
                                severity: 'warning',
                            })
                        }
                    />
                </Box>

                {/* Right side buttons group */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        alignItems: 'center',
                        flexShrink: 0,
                        marginLeft: 'auto',
                    }}
                >
                    {/* Theme Toggle */}
                    <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                        <IconButton
                            onClick={toggleMode}
                            sx={{
                                color: 'white',
                                padding: { xs: '6px', sm: '8px' },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    </Tooltip>

                    {/* Tools Menu */}
                    <Button
                        id="tools-button"
                        aria-controls={toolsOpen ? 'tools-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={toolsOpen ? 'true' : undefined}
                        onClick={handleToolsClick}
                        variant="text"
                        sx={{
                            minWidth: 'auto',
                            padding: { xs: '6px', sm: '8px' },
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <BuildIcon />
                    </Button>
                    <Menu
                        id="tools-menu"
                        anchorEl={toolsAnchorEl}
                        open={toolsOpen}
                        onClose={handleToolsClose}
                        MenuListProps={{
                            'aria-labelledby': 'tools-button',
                        }}
                        TransitionComponent={Fade}
                    >
                        <MenuItem
                            onClick={handleToolsClose}
                            component={Link}
                            to="/clipboard/"
                        >
                            <ContentPasteIcon sx={{ mr: 1 }} /> Clipboard
                        </MenuItem>
                        <MenuItem
                            onClick={handleToolsClose}
                            component={Link}
                            to="/downloads/"
                        >
                            <DownloadIcon sx={{ mr: 1 }} /> Downloads
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />

                        <MenuItem
                            onClick={() => {
                                handleDownloadChromeExtension();
                                handleToolsClose();
                            }}
                            disabled={isDownloadingChrome}
                        >
                            {isDownloadingChrome ? (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : (
                                <ExtensionIcon sx={{ mr: 1 }} />
                            )}
                            Chrome Extension
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleDownloadEdgeExtension();
                                handleToolsClose();
                            }}
                            disabled={isDownloadingEdge}
                        >
                            {isDownloadingEdge ? (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : (
                                <ExtensionIcon sx={{ mr: 1 }} />
                            )}
                            Edge Extension
                        </MenuItem>
                        <MenuItem
                            onClick={handleToolsClose}
                            component={Link}
                            target="_blank"
                            to="https://anakin.blackpixelworks.com/"
                        >
                            <TerminalIcon sx={{ mr: 1 }} /> Anakin Terminal
                        </MenuItem>
                    </Menu>

                    {/* Main Navigation Menu */}
                    <Button
                        id="basic-button"
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="contained"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0, sm: 1 },
                            minWidth: 'auto',
                            paddingX: { xs: 1, sm: 2 },
                        }}
                    >
                        <MenuIcon />
                        <Box
                            component="span"
                            sx={{ display: { xs: 'none', sm: 'inline' } }}
                        >
                            Menu
                        </Box>
                    </Button>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                        TransitionComponent={Fade}
                    >
                        <MenuItem onClick={handleClose} component={Link} to="/">
                            <HomeIcon sx={{ mr: 1 }} /> Home
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/dash/"
                        >
                            <DashboardIcon sx={{ mr: 1 }} /> Dashboard
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/bookmarks/"
                        >
                            <BookmarkIcon sx={{ mr: 1 }} /> Bookmarks
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/tags"
                        >
                            <LabelIcon sx={{ mr: 1 }} /> Tags
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/tasks/"
                        >
                            <Badge badgeContent={overdueCount || null} color="error" sx={{ mr: 1 }}>
                                <TaskIcon />
                            </Badge>
                            <Box component="span" sx={{ ml: 1 }}>Tasks</Box>
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/projects/"
                        >
                            <FolderIcon sx={{ mr: 1 }} /> Projects
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/weather/"
                        >
                            <CloudIcon sx={{ mr: 1 }} /> Weather
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/users/"
                        >
                            <PeopleIcon sx={{ mr: 1 }} /> Users
                        </MenuItem>
                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/ai"
                        >
                            <SmartToyIcon sx={{ mr: 1 }} /> AI Chat
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />

                        <MenuItem
                            onClick={handleClose}
                            component={Link}
                            to="/logout"
                        >
                            <LogoutIcon sx={{ mr: 1 }} /> Logout
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <CustomAlert
                message={alertInfo.message}
                severity={alertInfo.severity}
            />
        </>
    );
}
